import { FlightSetType } from 'types'

const MIN_FILE_SIZE = 5 * 1024
const VALIDATION_SAMPLE_SIZE = 5
const VALIDATION_CHUNK_SIZE = 500
const VARIETY_THRESHOLD = 2

export interface ValidationResult {
  isValid: boolean
  errors: string[]
  warnings: string[]
}

export const validateFileSize = (file: File): boolean => {
  return file.size > MIN_FILE_SIZE
}

export const validatePixelVariety = async (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image()
    img.decoding = 'async'
    const canvas = document.createElement('canvas')
    const objectUrl = URL.createObjectURL(file)
    const cleanupTasks: Array<() => void> = []
    let isResolved = false

    const complete = (result: boolean) => {
      if (isResolved) return
      isResolved = true
      URL.revokeObjectURL(objectUrl)
      cleanupTasks.forEach((fn) => fn())
      img.src = ''
      canvas.width = 0
      canvas.height = 0
      resolve(result)
    }

    img.onload = () => {
      try {
        const width = Math.min(img.width, 100)
        const height = Math.min(img.height, 100)

        canvas.width = width
        canvas.height = height
        const context = canvas.getContext('2d', { willReadFrequently: false })

        if (!context) {
          complete(true)
          return
        }

        context.clearRect(0, 0, width, height)
        context.drawImage(img, 0, 0, width, height)
        const { data } = context.getImageData(0, 0, width, height)

        let count = 0
        let mean = 0
        let m2 = 0

        for (let i = 0; i < data.length; i += 4) {
          const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3
          count += 1
          const delta = brightness - mean
          mean += delta / count
          const delta2 = brightness - mean
          m2 += delta * delta2
        }

        const variance = count > 0 ? m2 / count : 0
        complete(Math.sqrt(variance) > VARIETY_THRESHOLD)
      } catch {
        complete(true)
      }
    }

    const handleError = () => {
      complete(false)
    }

    img.addEventListener('error', handleError)
    cleanupTasks.push(() => {
      img.removeEventListener('error', handleError)
      img.onload = null
    })

    img.src = objectUrl
  })
}

export const validateIRFilename = (
  filename: string,
  setType: FlightSetType
): boolean => {
  if (setType === 'day-ir' || setType === 'night-ir') {
    return filename.match(/_T\.(jpg|jpeg|JPG|JPEG)$/i) !== null
  }
  return true
}

export const isWFile = (filename: string): boolean => {
  return filename.match(/_W\.(jpg|jpeg|JPG|JPEG)$/i) !== null
}

export const validateFiles = async (
  files: File[],
  setType: FlightSetType
): Promise<ValidationResult> => {
  const errors: string[] = []
  const warnings: string[] = []

  let tooSmallCount = 0
  let wrongFilenameCount = 0
  let blankImageCount = 0

  const yieldToBrowser = async () =>
    new Promise<void>((resolve) => {
      setTimeout(resolve, 0)
    })

  const sampleSize = Math.min(VALIDATION_SAMPLE_SIZE, files.length)
  let blankCheckCount = 0

  for (let index = 0; index < files.length; index += 1) {
    const file = files[index]
    if (!validateFileSize(file)) {
      tooSmallCount++
    }

    if (!validateIRFilename(file.name, setType)) {
      wrongFilenameCount++
    }

    if ((index + 1) % VALIDATION_CHUNK_SIZE === 0) {
      await yieldToBrowser()
    }
  }

  for (let index = 0; index < files.length; index += 1) {
    if (blankCheckCount >= sampleSize) {
      break
    }

    const sampleFile = files[index]

    const hasPixelVariety = await validatePixelVariety(sampleFile)
    blankCheckCount += 1
    if (!hasPixelVariety) {
      blankImageCount++
    }
  }

  if (tooSmallCount > 0) {
    errors.push(`${tooSmallCount} file(s) are too small (< 5KB)`)
  }

  if (
    wrongFilenameCount > 0 &&
    (setType === 'day-ir' || setType === 'night-ir')
  ) {
    errors.push(
      `${wrongFilenameCount} file(s) don't have the required _T naming for IR images`
    )
  }

  if (blankImageCount > 0) {
    errors.push(
      `${blankImageCount} of the first ${sampleSize} sampled image(s) appear blank or corrupted`
    )
    warnings.push(
      `Only the first ${sampleSize} images were checked for pixel variety. Other images may have similar issues.`
    )
  }

  const isValid = errors.length === 0

  return {
    isValid,
    errors,
    warnings,
  }
}
